/** @param {import('../../config.js').CONFIG['city']} config */
export function blockStride(config) {
  return config.lotSize + config.roadWidth;
}

/**
 * Infinite-grid lot rect (global lot indices gx, gz).
 * @param {number} gx
 * @param {number} gz
 * @param {import('../../config.js').CONFIG['city']} config
 */
export function lotWorldRectGlobal(gx, gz, config) {
  const stride = blockStride(config);
  const minX = config.roadWidth + gx * stride;
  const minZ = config.roadWidth + gz * stride;
  const maxX = minX + config.lotSize;
  const maxZ = minZ + config.lotSize;
  return {
    minX,
    minZ,
    maxX,
    maxZ,
    centerX: (minX + maxX) / 2,
    centerZ: (minZ + maxZ) / 2,
    width: config.lotSize,
    depth: config.lotSize,
    gx,
    gz,
  };
}

/** @param {number} roadIndex @param {import('../../config.js').CONFIG['city']} config */
export function verticalRoadCenterX(roadIndex, config) {
  return roadIndex * blockStride(config) + config.roadWidth / 2;
}

/** @param {number} roadIndex @param {import('../../config.js').CONFIG['city']} config */
export function horizontalRoadCenterZ(roadIndex, config) {
  return roadIndex * blockStride(config) + config.roadWidth / 2;
}

/**
 * @param {number} chunkWorldSize Meters per streaming chunk (world XZ)
 */
export function getChunkBounds(chunkX, chunkZ, chunkWorldSize) {
  const minX = chunkX * chunkWorldSize - chunkWorldSize * 0.5;
  const minZ = chunkZ * chunkWorldSize - chunkWorldSize * 0.5;
  return {
    minX,
    minZ,
    maxX: minX + chunkWorldSize,
    maxZ: minZ + chunkWorldSize,
    centerX: minX + chunkWorldSize / 2,
    centerZ: minZ + chunkWorldSize / 2,
    chunkX,
    chunkZ,
  };
}

export function worldToChunkCoord(x, z, chunkWorldSize) {
  const bias = chunkWorldSize * 0.5;
  return {
    cx: Math.floor((x + bias) / chunkWorldSize),
    cz: Math.floor((z + bias) / chunkWorldSize),
  };
}

export function chunkKey(cx, cz) {
  return `${cx},${cz}`;
}

/** Chebyshev distance between chunk indices. */
export function chunkChebyshevDist(cx, cz, pcx, pcz) {
  return Math.max(Math.abs(cx - pcx), Math.abs(cz - pcz));
}

/**
 * @param {number} chunkX
 * @param {number} chunkZ
 * @param {number} chunkWorldSize
 */
export function chunkCenterWorld(chunkX, chunkZ, chunkWorldSize) {
  const bounds = getChunkBounds(chunkX, chunkZ, chunkWorldSize);
  return { x: bounds.centerX, z: bounds.centerZ };
}

/** Detail tier from distance to world origin in lot space. */
export function lotDetailTierGlobal(gx, gz) {
  const dist = (Math.hypot(gx, gz) * 2) / 24;
  if (dist < 0.38) {
    return { cellsPerMeter: 7, maxBuildings: 3, shape: 'building' };
  }
  if (dist < 0.72) {
    return { cellsPerMeter: 5, maxBuildings: 2, shape: 'building' };
  }
  return { cellsPerMeter: 3, maxBuildings: 1, shape: 'facade' };
}

export function lotCenterInBounds(lot, bounds, border = 2) {
  return (
    lot.centerX >= bounds.minX + border &&
    lot.centerX <= bounds.maxX - border &&
    lot.centerZ >= bounds.minZ + border &&
    lot.centerZ <= bounds.maxZ - border
  );
}

export function rangeOverlaps(aMin, aMax, bMin, bMax) {
  return aMax > bMin && aMin < bMax;
}

/**
 * True when world XZ lies on a road strip (vertical or horizontal grid band).
 * @param {number} x
 * @param {number} z
 * @param {import('../../config.js').CONFIG['city']} config
 */
export function isPositionOnRoad(x, z, config) {
  const stride = blockStride(config);
  const w = config.roadWidth;
  const localX = ((x % stride) + stride) % stride;
  const localZ = ((z % stride) + stride) % stride;
  return localX < w || localZ < w;
}

/** @param {number} x @param {import('../../config.js').CONFIG['city']} config */
export function distanceToNearestVerticalRoadCenter(x, config) {
  const stride = blockStride(config);
  const center = config.roadWidth / 2;
  const idx = Math.round((x - center) / stride);
  const nearestCenter = idx * stride + center;
  return Math.abs(x - nearestCenter);
}

/** @param {number} z @param {import('../../config.js').CONFIG['city']} config */
export function distanceToNearestHorizontalRoadCenter(z, config) {
  const stride = blockStride(config);
  const center = config.roadWidth / 2;
  const idx = Math.round((z - center) / stride);
  const nearestCenter = idx * stride + center;
  return Math.abs(z - nearestCenter);
}

/**
 * Road surface or sidewalk band beside a street (where NPCs actually walk).
 * @param {number} x
 * @param {number} z
 * @param {import('../../config.js').CONFIG['city']} config
 */
export function isPositionInRoadPedestrianCorridor(x, z, config) {
  if (isPositionOnRoad(x, z, config)) return true;
  const reach =
    config.roadWidth * 0.5 + config.sidewalkWidth + 2.2;
  return (
    distanceToNearestVerticalRoadCenter(x, config) <= reach ||
    distanceToNearestHorizontalRoadCenter(z, config) <= reach
  );
}
